<template>
  <div class="sena-tickets">
    <va-card :title="cardTitle">
      <template slot="actions">
        <va-button
          color="info"
          icon="fa fa-shield"
          :loading="auditing"
          @click="runAudit"
        >
          {{ $t('sena.audit.button') }}
        </va-button>
      </template>

      <!-- Painel de Auditoria de Integridade -->
      <div
        v-if="showAuditPanel && auditResult"
        class="sena-audit-panel mb-4"
        :class="auditResult.isOk ? 'sena-audit-panel--ok' : 'sena-audit-panel--fail'"
      >
        <!-- Cabeçalho do Painel -->
        <div class="sena-audit-header">
          <div class="d-flex align-items-center">
            <div
              class="sena-audit-icon-box"
              :class="auditResult.isOk ? 'sena-audit-icon-box--ok' : 'sena-audit-icon-box--fail'"
            >
              <i
                class="fa"
                :class="auditResult.isOk ? 'fa-shield text-success' : 'fa-exclamation-triangle text-danger'"
              />
            </div>
            <div class="ml-3">
              <div class="d-flex align-items-center flex-wrap">
                <h5 class="sena-audit-title mb-0 mr-2">
                  {{ $t('sena.audit.title') }}
                </h5>
                <va-badge
                  :color="auditResult.isOk ? 'success' : 'danger'"
                  class="font-weight-bold"
                >
                  {{ auditResult.isOk ? $t('sena.audit.successTitle') : `${auditResult.anomalies.length} ${$t('sena.audit.anomalies')}` }}
                </va-badge>
              </div>
              <small class="text-muted d-block mt-1">
                Auditado em {{ $moment(auditResult.auditedAt).format('DD/MM/YYYY [às] HH:mm:ss') }}
              </small>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <va-button
              flat
              size="small"
              color="info"
              icon="fa fa-refresh"
              :loading="auditing"
              @click="runAudit"
              class="mr-2"
            />
            <va-button
              flat
              size="small"
              color="gray"
              icon="fa fa-times"
              @click="showAuditPanel = false"
            />
          </div>
        </div>

        <!-- Grid de 4 Métricas de Segurança -->
        <div class="row sena-audit-grid">
          <!-- 1. Assinaturas SHA-256 -->
          <div class="flex xs12 sm6 md3">
            <div
              class="sena-metric-card"
              :class="auditResult.validSignatures === auditResult.total ? 'sena-metric-card--ok' : 'sena-metric-card--fail'"
            >
              <div class="d-flex align-items-center mb-2">
                <div class="sena-metric-icon-box sena-metric-icon-box--primary mr-2">
                  <i class="fa fa-key" />
                </div>
                <span class="sena-metric-card__title">{{ $t('sena.audit.validSignatures') }}</span>
              </div>
              <div class="sena-metric-card__value">
                {{ auditResult.validSignatures }}/{{ auditResult.total }}
              </div>
              <div class="sena-metric-card__footer">
                <span
                  class="sena-status-tag"
                  :class="auditResult.validSignatures === auditResult.total ? 'sena-status-tag--ok' : 'sena-status-tag--fail'"
                >
                  {{ auditResult.total ? Math.round((auditResult.validSignatures / auditResult.total) * 100) : 100 }}% Válidas
                </span>
              </div>
            </div>
          </div>

          <!-- 2. Conformidade de Lockout -->
          <div class="flex xs12 sm6 md3">
            <div
              class="sena-metric-card"
              :class="auditResult.validLockout === auditResult.total ? 'sena-metric-card--ok' : 'sena-metric-card--fail'"
            >
              <div class="d-flex align-items-center mb-2">
                <div class="sena-metric-icon-box sena-metric-icon-box--warning mr-2">
                  <i class="fa fa-clock-o" />
                </div>
                <span class="sena-metric-card__title">{{ $t('sena.audit.lockoutCompliance') }}</span>
              </div>
              <div class="sena-metric-card__value">
                {{ auditResult.validLockout }}/{{ auditResult.total }}
              </div>
              <div class="sena-metric-card__footer">
                <span
                  class="sena-status-tag"
                  :class="auditResult.validLockout === auditResult.total ? 'sena-status-tag--ok' : 'sena-status-tag--fail'"
                >
                  {{ auditResult.total ? Math.round((auditResult.validLockout / auditResult.total) * 100) : 100 }}% no prazo
                </span>
              </div>
            </div>
          </div>

          <!-- 3. Rigor de 6 Palpites -->
          <div class="flex xs12 sm6 md3">
            <div
              class="sena-metric-card"
              :class="auditResult.validItemCount === auditResult.total ? 'sena-metric-card--ok' : 'sena-metric-card--fail'"
            >
              <div class="d-flex align-items-center mb-2">
                <div class="sena-metric-icon-box sena-metric-icon-box--info mr-2">
                  <i class="fa fa-list-ol" />
                </div>
                <span class="sena-metric-card__title">{{ $t('sena.audit.sixGuesses') }}</span>
              </div>
              <div class="sena-metric-card__value">
                {{ auditResult.validItemCount }}/{{ auditResult.total }}
              </div>
              <div class="sena-metric-card__footer">
                <span
                  class="sena-status-tag"
                  :class="auditResult.validItemCount === auditResult.total ? 'sena-status-tag--ok' : 'sena-status-tag--fail'"
                >
                  {{ auditResult.total ? Math.round((auditResult.validItemCount / auditResult.total) * 100) : 100 }}% completos
                </span>
              </div>
            </div>
          </div>

          <!-- 4. Unicidade de Participante -->
          <div class="flex xs12 sm6 md3">
            <div
              class="sena-metric-card"
              :class="auditResult.duplicateClientsCount === 0 ? 'sena-metric-card--ok' : 'sena-metric-card--fail'"
            >
              <div class="d-flex align-items-center mb-2">
                <div class="sena-metric-icon-box sena-metric-icon-box--success mr-2">
                  <i class="fa fa-user-circle" />
                </div>
                <span class="sena-metric-card__title">{{ $t('sena.audit.singleTicket') }}</span>
              </div>
              <div class="sena-metric-card__value">
                {{ auditResult.uniqueClients }}
              </div>
              <div class="sena-metric-card__footer">
                <span
                  class="sena-status-tag"
                  :class="auditResult.duplicateClientsCount === 0 ? 'sena-status-tag--ok' : 'sena-status-tag--fail'"
                >
                  {{ auditResult.duplicateClientsCount === 0 ? '0 duplicados' : `${auditResult.duplicateClientsCount} duplicados` }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabela Estruturada de Inconsistências / Anomalias -->
        <div v-if="auditResult.anomalies.length > 0" class="sena-anomalies-box mt-3">
          <div class="sena-anomalies-header">
            <i class="fa fa-exclamation-circle text-danger mr-2" />
            <b>Inconsistências Detectadas ({{ auditResult.anomalies.length }})</b>
          </div>
          <div class="table-responsive">
            <table class="table sena-anomalies-table mb-0">
              <thead>
                <tr>
                  <th>Participante</th>
                  <th>Bilhete</th>
                  <th>Tipo</th>
                  <th>Ocorrência</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(a, idx) in auditResult.anomalies" :key="idx">
                  <td>
                    <b>{{ a.clientName }}</b>
                    <small class="text-muted d-block">ID #{{ a.clientId }}</small>
                  </td>
                  <td>
                    <code class="sena-hash-pill" :title="a.ticketId">
                      {{ shortHash(a.ticketId) }}
                    </code>
                  </td>
                  <td>
                    <va-badge color="danger" class="text-uppercase font-weight-bold">
                      {{ a.typeLabel || 'Alerta' }}
                    </va-badge>
                  </td>
                  <td class="sena-anomaly-desc">
                    <div>{{ a.motivo }}</div>
                    <small v-if="a.regra" class="text-muted d-block mt-1">{{ a.regra }}</small>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="row mb-3">
        <div class="flex md6">
          <va-input
            v-model="search"
            :label="$t('sena.forms.searchClientLabel')"
            :placeholder="$t('sena.forms.searchClientPlaceholder')"
          />
        </div>
      </div>

      <va-data-table
        :fields="fields"
        :data="tickets"
        :per-page="perPage"
        :totalPages="totalPages"
        :loading="loading"
        @page-selected="showMore"
        no-data-label="Nenhum bilhete enviado"
        api-mode
      >
        <template slot="assinatura" slot-scope="props">
          <code class="sena-hash" :title="props.rowData.hash_signature">
            {{ shortHash(props.rowData.hash_signature) }}
          </code>
          <va-badge v-if="props.rowData.hash_signature === 'PENDING'" color="danger">
            {{ $t('sena.labels.hashPending') }}
          </va-badge>
        </template>

        <template slot="situacao" slot-scope="props">
          <va-badge v-if="props.rowData.is_invalidated" color="gray">
            {{ $t('sena.labels.invalidated') }}
          </va-badge>
          <va-badge v-else-if="props.rowData.is_winner" color="success">
            {{ $t(props.rowData.hits === 6 ? 'sena.labels.winner' : 'sena.labels.winner5') }}
          </va-badge>
          <!--
            is_eliminated é `misses > 0`: perdeu a faixa de 6, NÃO está fora.
            Com 1 erro o bilhete ainda concorre à faixa de 5; fora mesmo só com
            2 erros.
          -->
          <va-badge v-else :color="badgeCor(props.rowData)">
            {{ $t(badgeRotulo(props.rowData)) }}
          </va-badge>
        </template>

        <template slot="actions" slot-scope="props">
          <va-popover :message="`${$t('sena.tooltips.showItems')}`" placement="bottom">
            <va-button
              flat
              color="gray"
              icon="fa fa-eye"
              @click="openItems(props.rowData)"
            />
          </va-popover>
        </template>
      </va-data-table>
    </va-card>

    <va-modal
      v-model="modalItems"
      size="medium"
      :title="$t('sena.modals.itemsTitle')"
      :okText="$t('modals.close')"
      :hideDefaultActions="false"
      noOutsideDismiss
    >
      <div v-if="selected">
        <!-- Selo de Autenticidade e Trilha de Auditoria -->
        <div class="sena-ticket-audit-box p-3 mb-3 rounded">
          <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
            <div class="sena-auth-badge">
              <i class="fa fa-shield mr-2" />
              <span>{{ $t('sena.audit.authenticityBadge') }}</span>
            </div>
            <va-button
              size="small"
              color="info"
              :icon="hashCopied ? 'fa fa-check' : 'fa fa-copy'"
              @click="copyHash(selected.hash_signature)"
            >
              {{ hashCopied ? $t('sena.audit.hashCopied') : $t('sena.audit.copyHash') }}
            </va-button>
          </div>
          <div class="small text-muted mb-1 font-weight-bold">
            Assinatura HMAC/SHA-256:
          </div>
          <code class="d-block p-2 bg-white rounded border sena-hash-code text-break">{{ selected.hash_signature }}</code>
        </div>

        <p class="mb-2">
          <b>{{ $t('sena.headings.clientId') }}:</b> {{ selected.client_id }}
          &nbsp;—&nbsp;
          <b>{{ $t('sena.headings.hits') }}:</b> {{ selected.hits }}
          &nbsp;/&nbsp;
          <b>{{ $t('sena.headings.misses') }}:</b> {{ selected.misses }}
        </p>
        <table class="sena-items">
          <thead>
            <tr>
              <th>{{ $t('sena.headings.match') }}</th>
              <th>{{ $t('sena.headings.guess') }}</th>
              <th>{{ $t('sena.headings.score') }}</th>
              <th>{{ $t('sena.headings.hit') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in selectedItems" :key="item.id">
              <td>{{ item.game.club1.name }} x {{ item.game.club2.name }}</td>
              <td class="text-center"><b>{{ item.pts_club1 }} x {{ item.pts_club2 }}</b></td>
              <td class="text-center">
                <span v-if="item.game.results && item.game.results.length">
                  {{ item.game.results[0].pts_club1 }} x {{ item.game.results[0].pts_club2 }}
                </span>
                <span v-else class="sena-muted">—</span>
              </td>
              <td class="text-center">
                <!-- is_exact_match NULL = jogo sem placar ou cancelado.
                     NULL não é erro: não elimina ninguém. -->
                <span v-if="item.is_exact_match === true" class="sena-ok">✓</span>
                <span v-else-if="item.is_exact_match === false" class="sena-bad">✕</span>
                <span v-else class="sena-muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </va-modal>
  </div>
</template>

<script>
import gql from 'graphql-tag'

const perPage = 15

export default {
  name: 'SenaEditionTickets',
  props: {
    edition: { required: true },
  },
  data () {
    return {
      tickets: [],
      pages: null,
      editionRow: null,
      selected: null,
      selectedItems: [],
      modalItems: false,
      page: 1,
      perPage,
      loading: false,
      search: '',
      auditing: false,
      auditResult: null,
      showAuditPanel: false,
      hashCopied: false,
    }
  },
  computed: {
    where () {
      const w = { sena_edition_id: { _eq: Number(this.edition) } }
      const term = this.search && this.search.trim()
      if (term) {
        if (/^\d+$/.test(term)) {
          w.client_id = { _eq: Number(term) }
        } else {
          w.client = { firstName: { _ilike: `%${term}%` } }
        }
      }
      return w
    },
    cardTitle () {
      return this.editionRow
        ? `${this.$t('sena.cards.tickets')} — ${this.editionRow.name}`
        : this.$t('sena.cards.tickets')
    },
    totalPages () {
      return this.pages != null ? Math.ceil(this.pages.aggregate.count / this.perPage) : 0
    },
    fields () {
      const moment = this.$moment
      return [
        { name: 'client_id', title: this.$t('sena.headings.clientId'), width: '100px', dataClass: 'text-center' },
        {
          name: 'client',
          title: this.$t('sena.headings.name'),
          callback (value) {
            return value ? `${value.firstName || ''} ${value.lastName || ''}`.trim() : '—'
          },
        },
        { name: 'hits', title: this.$t('sena.headings.hits'), width: '80px', dataClass: 'text-center' },
        { name: 'misses', title: this.$t('sena.headings.misses'), width: '80px', dataClass: 'text-center' },
        {
          name: '__slot:situacao',
          title: this.$t('sena.headings.situation'),
          width: '130px',
          dataClass: 'text-center',
          titleClass: 'text-center',
        },
        { name: '__slot:assinatura', title: this.$t('sena.headings.signature'), width: '160px' },
        {
          name: 'created_at',
          title: this.$t('sena.headings.sentAt'),
          width: '170px',
          callback (value) {
            return moment(value).format('DD/MM/YYYY [às] HH:mm:ss')
          },
        },
        {
          name: '__slot:actions',
          title: this.$t('tables.actions'),
          dataClass: 'text-center',
          titleClass: 'text-center',
          width: '80px',
        },
      ]
    },
  },
  apollo: {
    editionRow: {
      query: gql`
        query SenaTicketsHead($edition: Int!) {
          editionRow: sena_edition_by_pk(id: $edition) { id name status lockout_at }
        }
      `,
      variables () {
        return { edition: Number(this.edition) }
      },
      update: data => data.editionRow,
    },
    tickets: {
      query: gql`
        query SenaTickets($page: Int = 0, $perPage: Int = 15, $where: sena_ticket_bool_exp!) {
          tickets: sena_ticket(limit: $perPage, offset: $page, where: $where, order_by: {created_at: asc}) {
            id
            client_id
            hits
            misses
            is_eliminated
            is_winner
            is_invalidated
            hash_signature
            created_at
            client { id firstName lastName email }
          }
        }
      `,
      variables () {
        return { perPage, page: 0, where: this.where }
      },
    },
    pages: {
      query: gql`
        query SenaTicketsCount($where: sena_ticket_bool_exp!) {
          pages: sena_ticket_aggregate(where: $where) {
            aggregate { count }
          }
        }
      `,
      variables () {
        return { where: this.where }
      },
    },
  },
  methods: {
    badgeRotulo (row) {
      if (row.misses >= 2) { return 'sena.labels.eliminated' }
      if (row.misses === 1) { return 'sena.labels.alive5' }
      return 'sena.labels.alive'
    },
    badgeCor (row) {
      if (row.misses >= 2) { return 'danger' }
      if (row.misses === 1) { return 'warning' }
      return 'info'
    },
    shortHash (h) {
      if (!h) return '—'
      return h === 'PENDING' ? h : `${h.slice(0, 8)}…${h.slice(-6)}`
    },
    showMore (page) {
      this.loading = true
      this.page = page
      this.$apollo.queries.tickets.fetchMore({
        variables: {
          page: (page - 1) * this.perPage,
          perPage: this.perPage,
          where: this.where,
        },
        updateQuery: (_, { fetchMoreResult }) => {
          this.loading = false
          return fetchMoreResult
        },
      })
    },
    openItems (row) {
      this.selected = row
      this.selectedItems = []
      this.modalItems = true
      this.$apollo.addSmartQuery('senaItems', {
        query: gql`
          query SenaTicketItems($ticket: uuid!) {
            items: sena_ticket_item(where: {sena_ticket_id: {_eq: $ticket}}, order_by: {game_id: asc}) {
              id
              game_id
              pts_club1
              pts_club2
              is_exact_match
              game {
                id
                datetime
                club1 { id name }
                club2 { id name }
                results { id pts_club1 pts_club2 }
              }
            }
          }
        `,
        variables: { ticket: row.id },
        fetchPolicy: 'network-only',
        manual: true,
        result ({ data, loading }) {
          if (!loading && data) this.selectedItems = data.items
        },
      })
    },
    copyHash (hash) {
      if (!hash) return
      navigator.clipboard.writeText(hash).then(() => {
        this.hashCopied = true
        setTimeout(() => { this.hashCopied = false }, 2500)
      })
    },
    async runAudit () {
      this.auditing = true
      this.showAuditPanel = true
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query SenaFullAudit($edition: Int!) {
              edition: sena_edition_by_pk(id: $edition) {
                id
                name
                lockout_at
                status
              }
              tickets: sena_ticket(where: {sena_edition_id: {_eq: $edition}}) {
                id
                client_id
                hash_signature
                created_at
                is_invalidated
                client { id firstName lastName email }
              }
            }
          `,
          variables: { edition: Number(this.edition) },
          fetchPolicy: 'network-only',
        })

        const ed = data.edition
        const tickets = data.tickets || []
        const lockoutTs = ed && ed.lockout_at ? new Date(ed.lockout_at).getTime() : null

        let items = []
        const ticketIds = tickets.map(t => t.id)
        if (ticketIds.length > 0) {
          const itemsRes = await this.$apollo.query({
            query: gql`
              query SenaAuditItems($ticketIds: [uuid!]!) {
                items: sena_ticket_item(where: {sena_ticket_id: {_in: $ticketIds}}) {
                  id
                  sena_ticket_id
                  game_id
                  pts_club1
                  pts_club2
                }
              }
            `,
            variables: { ticketIds },
            fetchPolicy: 'network-only',
          })
          items = (itemsRes.data && itemsRes.data.items) || []
        }

        const itemsByTicket = {}
        for (const it of items) {
          if (!itemsByTicket[it.sena_ticket_id]) {
            itemsByTicket[it.sena_ticket_id] = []
          }
          itemsByTicket[it.sena_ticket_id].push(it)
        }

        let validSignatures = 0
        let validLockout = 0
        let validItemCount = 0
        const clientSet = new Set()
        const duplicateClients = new Set()
        const anomalies = []

        for (const t of tickets) {
          const clientName = t.client
            ? `${t.client.firstName || ''} ${t.client.lastName || ''}`.trim()
            : `Cliente #${t.client_id}`

          // 1. Checagem de Assinatura SHA-256 (64 hex chars, não PENDING)
          const isHex64 = /^[0-9a-fA-F]{64}$/.test(t.hash_signature || '')
          if (isHex64 && t.hash_signature !== 'PENDING') {
            validSignatures++
          } else {
            anomalies.push({
              ticketId: t.id,
              clientId: t.client_id,
              clientName,
              typeLabel: 'Assinatura Inválida',
              motivo: `Assinatura ausente ou fora do formato SHA-256: "${t.hash_signature || 'vazia'}"`,
              regra: 'Cada bilhete deve conter um hash HMAC/SHA-256 de 64 caracteres hexadecimais.',
            })
          }

          // 2. Checagem de Lockout
          const ticketTs = t.created_at ? new Date(t.created_at).getTime() : null
          if (!lockoutTs || (ticketTs && ticketTs <= lockoutTs)) {
            validLockout++
          } else {
            anomalies.push({
              ticketId: t.id,
              clientId: t.client_id,
              clientName,
              typeLabel: 'Lockout Excedido',
              motivo: `Enviado em ${this.$moment(t.created_at).format('DD/MM/YYYY [às] HH:mm:ss')}`,
              regra: `Horário limite de fechamento: ${this.$moment(ed.lockout_at).format('DD/MM/YYYY [às] HH:mm:ss')}`,
            })
          }

          // 3. Checagem de 6 Palpites
          const count = (itemsByTicket[t.id] || []).length
          if (count === 6) {
            validItemCount++
          } else {
            anomalies.push({
              ticketId: t.id,
              clientId: t.client_id,
              clientName,
              typeLabel: 'Palpites Incompletos',
              motivo: `Contém ${count} palpites cadastrados`,
              regra: 'O bilhete exige rigorosamente 6 palpites de jogos.',
            })
          }

          // 4. Unicidade por Cliente
          if (clientSet.has(t.client_id)) {
            duplicateClients.add(t.client_id)
            anomalies.push({
              ticketId: t.id,
              clientId: t.client_id,
              clientName,
              typeLabel: 'Bilhete Duplicado',
              motivo: 'O participante possui mais de um bilhete submetido na mesma edição.',
              regra: 'É permitido estritamente 1 bilhete por assinante por rodada.',
            })
          } else {
            clientSet.add(t.client_id)
          }
        }

        const total = tickets.length
        const isOk = total === 0 || (
          validSignatures === total &&
          validLockout === total &&
          validItemCount === total &&
          duplicateClients.size === 0 &&
          anomalies.length === 0
        )

        this.auditResult = {
          total,
          validSignatures,
          validLockout,
          validItemCount,
          uniqueClients: clientSet.size,
          duplicateClientsCount: duplicateClients.size,
          anomalies,
          isOk,
          auditedAt: new Date(),
        }
      } catch (err) {
        console.error('Erro na auditoria:', err)
        this.showToast('Erro ao realizar a auditoria da edição', { color: 'danger' })
      } finally {
        this.auditing = false
      }
    },
  },
}
</script>

<style lang="scss">
.sena-hash {
  font-size: $font-size-mini;
}

.sena-hash-full code {
  font-size: $font-size-mini;
  word-break: break-all;
}

/* Painel Principal de Auditoria */
.sena-audit-panel {
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  padding: 1.25rem 1.5rem;
  transition: all 0.3s ease;

  &--ok {
    border-top: 4px solid #28a745;
  }

  &--fail {
    border-top: 4px solid #e53935;
    background-color: #fffdfd;
  }
}

.sena-audit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #edf2f7;
}

.sena-audit-title {
  font-weight: 700;
  color: #1a202c;
  font-size: 1.15rem;
}

.sena-audit-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;

  &--ok {
    background-color: #e6f4ea;
  }

  &--fail {
    background-color: #fce8e6;
  }
}

/* Grid de Cards de Métricas */
.sena-audit-grid {
  margin-bottom: 0.5rem;
}

.sena-metric-icon-box {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;

  &--primary {
    background-color: #ebf5ff;
    color: #007bff;
  }

  &--warning {
    background-color: #fff8e6;
    color: #d97706;
  }

  &--info {
    background-color: #e6f7ff;
    color: #0284c7;
  }

  &--success {
    background-color: #e6f4ea;
    color: #16a34a;
  }
}

.sena-metric-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem 1.15rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }

  &__title {
    font-size: 0.78rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    line-height: 1.2;
  }

  &__value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.4rem;
  }

  &--ok &__value {
    color: #1e293b;
  }

  &--fail &__value {
    color: #e53935;
  }
}

.sena-status-tag {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.55rem;
  border-radius: 6px;

  &--ok {
    background-color: #e6f4ea;
    color: #1e7e34;
  }

  &--fail {
    background-color: #fce8e6;
    color: #c5221f;
  }
}

/* Tabela de Anomalias */
.sena-anomalies-box {
  background-color: #ffffff;
  border: 1px solid #fecaca;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(229, 57, 53, 0.05);
}

.sena-anomalies-header {
  background-color: #fef2f2;
  color: #991b1b;
  font-weight: 600;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #fecaca;
  font-size: 0.9rem;
}

.sena-anomalies-table {
  width: 100%;

  th {
    background-color: #f8fafc;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0.6rem 1rem;
    border-bottom: 1px solid #e2e8f0;
  }

  td {
    padding: 0.75rem 1rem;
    vertical-align: middle;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.875rem;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background-color: #fffbfb;
  }
}

.sena-hash-pill {
  font-size: 0.8rem;
  background-color: #f1f5f9;
  color: #334155;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.sena-anomaly-desc {
  color: #1e293b;
}

.sena-ticket-audit-box {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.sena-auth-badge {
  display: inline-flex;
  align-items: center;
  background-color: #e6f4ea;
  color: #137333;
  font-weight: 700;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #ceead6;
  line-height: 1.2;

  i {
    font-size: 0.9rem;
    color: #188038;
  }
}

.sena-hash-code {
  font-size: 0.85rem;
  color: #212529;
  letter-spacing: 0.5px;
}

.sena-items {
  width: 100%;
  margin-top: 0.75rem;

  th,
  td {
    padding: 0.4rem 0.6rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
}

.sena-ok {
  color: #4caf50;
  font-weight: bold;
}

.sena-bad {
  color: #e53935;
  font-weight: bold;
}

.sena-muted {
  color: #999999;
}
</style>
